import type { TFunction } from 'i18next';

import {
  BLACKBAUD_MARKETPLACE_URL,
  EBlackbaudRaisersEdgeUnsubscribeSyncActions,
  EBREContactMappings,
  RAISERS_EDGE_CONTACT_DATA_TYPES,
} from '@feathr/blackbox';
import type { IIntegrationUIConfig } from '@feathr/components';
import {
  getCrmMappingFields,
  getCrmTabs,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmConfig.utils';
import { RaisersEdgeContactLabelMap } from '@feathr/extender/styles/blackbaud_raisers_edge';

/** Settings field keys for the unsubscribe preference. They match `IBlackbaudUnsubscribeSyncPreference`. */
export const UNSUBSCRIBE_ACTION_KEY = 'action';
export const SOLICIT_CODE_KEY = 'solicit_code';

/** Toggle keys that map to model attributes. The Action toggles have none yet. */
export const CONSTITUENT_DOWNSYNC_KEY = 'constituents';
export const GIFT_DOWNSYNC_KEY = 'gift_events';
export const DONATION_UPSYNC_KEY = 'donations';
export const FORM_SUBMISSION_UPSYNC_KEY = 'people_from_form_submissions';
/** The fund field on donation upsync. It matches `IFundraising`. */
export const FUND_KEY = 'fund_id';

/** Mappings tab section keys. The tab puts constituent sync above the mapping table and create constituents below it. */
export const CONSTITUENT_SYNC_SECTION_KEY = 'constituent_sync';
export const CREATE_CONSTITUENTS_SECTION_KEY = 'create_constituents';

/** Setup step keys, for the connect flow's handlers and links. */
export const ADD_APPLICATION_STEP_KEY = 'add_application';
export const CONNECT_STEP_KEY = 'connect_account';

/** Contact fields every integration maps. They can't be edited or removed. */
const REQUIRED_CONTACT_FIELDS: ReadonlySet<string> = new Set<EBREContactMappings>([
  EBREContactMappings.email,
  EBREContactMappings.id,
]);

/**
 * Raiser's Edge NXT v2 page config. Downsync, donation upsync, person upsync and unsubscribe sync
 * are live. The other upsync controls are disabled with a reason.
 */
export function getRaisersEdgeConfig(t: TFunction): IIntegrationUIConfig {
  const upsyncUnavailable = t('Syncing to Blackbaud is not available yet.');

  return {
    crmName: 'Blackbaud',
    mapping: {
      title: t('People'),
      crmRecordLabel: t('Constituents'),
      description: t(
        'Match Feathr people to Blackbaud constituents and choose which way each one syncs.',
      ),
      fields: getCrmMappingFields({
        dataTypes: RAISERS_EDGE_CONTACT_DATA_TYPES,
        getLabel: (key) => RaisersEdgeContactLabelMap(t, key),
        keys: Object.values(EBREContactMappings),
        requiredKeys: REQUIRED_CONTACT_FIELDS,
        upsyncUnavailable,
      }),
    },
    settingsSections: [
      {
        key: 'unsubscribes',
        heading: t('Manage unsubscribes'),
        description: t(
          'Select what should happen in Blackbaud when someone unsubscribes from an email in Feathr.',
        ),
        fields: [
          {
            key: UNSUBSCRIBE_ACTION_KEY,
            type: 'radio',
            label: t('When someone unsubscribes'),
            // The section heading names the group, so Figma shows no field label.
            hideLabel: true,
            options: [
              {
                id: EBlackbaudRaisersEdgeUnsubscribeSyncActions.DoNotSync,
                name: t('Do nothing in Blackbaud'),
                description: t(
                  'The constituent record in Blackbaud is left as is. Feathr keeps track of the unsubscribe on its own.',
                ),
              },
              {
                id: EBlackbaudRaisersEdgeUnsubscribeSyncActions.RequestsNoEmail,
                name: t('Add “Requests No Email” checkbox to constituent'),
                description: t(
                  'Checks the "Requests No Email" box on the constituent record in Blackbaud.',
                ),
              },
              {
                id: EBlackbaudRaisersEdgeUnsubscribeSyncActions.SolicitCode,
                name: t('Add solicit code to constituent'),
                description: t(
                  'Adds the solicit code you choose below to the constituent record in Blackbaud.',
                ),
              },
            ],
          },
          {
            key: SOLICIT_CODE_KEY,
            type: 'select',
            label: t('Solicit code'),
            placeholder: t('Select a code'),
            dependsOn: {
              key: UNSUBSCRIBE_ACTION_KEY,
              equals: EBlackbaudRaisersEdgeUnsubscribeSyncActions.SolicitCode,
            },
          },
        ],
      },
    ],
    setupSteps: [
      {
        key: ADD_APPLICATION_STEP_KEY,
        title: t('Add Feathr application'),
        description: t('Add the Feathr application to your Blackbaud account.'),
        descriptionLink: { label: t('Application marketplace'), href: BLACKBAUD_MARKETPLACE_URL },
        actionLabel: t('Confirm and continue'),
      },
      {
        key: CONNECT_STEP_KEY,
        title: t('Connect Blackbaud account'),
        description: t('Use your Blackbaud admin credentials to connect Blackbaud to Feathr.'),
        actionLabel: t('Connect Blackbaud'),
      },
    ],
    tabs: getCrmTabs(t),
    title: t('Blackbaud Raiser’s Edge NXT'),
    toggleSections: [
      {
        crmRecordLabel: t('Constituents'),
        description: t('Keep Feathr people up to date with their Blackbaud constituents.'),
        heading: t('People'),
        key: CONSTITUENT_SYNC_SECTION_KEY,
        syncRule: 'down_sync',
        tab: 'mappings',
        toggles: [
          {
            description: t(
              'When a constituent is created or updated in Blackbaud, the matching Feathr person is updated with the mapped fields below.',
            ),
            key: CONSTITUENT_DOWNSYNC_KEY,
            label: t('Sync Blackbaud constituents to Feathr'),
            // The crowbar e2e contract selects the v1 card by this name.
            name: 'constituent-sync-card',
          },
        ],
      },
      {
        crmRecordLabel: t('Constituents'),
        description: t(
          'Create new Blackbaud constituents from the Feathr people you choose below. A person who already matches a constituent is linked to it, not created again.',
        ),
        heading: t('People'),
        key: CREATE_CONSTITUENTS_SECTION_KEY,
        syncRule: 'up_sync',
        tab: 'mappings',
        toggles: [
          {
            description: t(
              'When someone submits a Feathr form, create them as a constituent in Blackbaud if they don’t already exist.',
            ),
            key: FORM_SUBMISSION_UPSYNC_KEY,
            label: t('Form submissions'),
            name: 'form-submission-upsync-card',
          },
        ],
      },
      {
        crmRecordLabel: t('Blackbaud'),
        description: t('Choose which activities sync to the matching constituent above.'),
        heading: t('Activities'),
        key: 'actions',
        syncRule: 'up_sync',
        tab: 'activities',
        toggles: [
          {
            description: t('Email opens sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'email_open',
            label: t('Email open'),
          },
          {
            description: t('Email clicks sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'email_click',
            label: t('Email click'),
          },
          {
            description: t('Form submissions sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'form_submission',
            label: t('Form submission'),
          },
          {
            description: t('Ad clicks sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'ad_click',
            label: t('Ad click'),
          },
        ],
      },
      {
        crmRecordLabel: t('Gifts'),
        description: t('Sync gift and donation activity with Blackbaud.'),
        heading: t('Donations'),
        key: 'gifts',
        syncRule: 'both_ways',
        tab: 'activities',
        toggles: [
          {
            description: t('Blackbaud gifts sync to the matching Feathr person record.'),
            key: GIFT_DOWNSYNC_KEY,
            label: t('Sync Blackbaud gifts to Feathr'),
            name: 'gift-downsync-card',
          },
          {
            description: t(
              'Feathr donations create or update the matching Blackbaud constituent and gift. Requires a fund.',
            ),
            field: {
              key: FUND_KEY,
              type: 'select',
              label: t('Fund'),
              placeholder: t('Search funds'),
              // `isFundMissing` enforces it: Save & Sync stays disabled until a fund is chosen.
              required: true,
            },
            key: DONATION_UPSYNC_KEY,
            label: t('Sync Feathr donations to Blackbaud'),
            name: 'donation-upsync-card',
          },
        ],
      },
    ],
  };
}
